import styles from './styles.module.css'
import React, { useLayoutEffect, useRef } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { Maximize2, Minimize2 } from 'lucide-react'
import { Button } from 'components/ui'
import useLocaleText from 'components/useLocaleText'
import { editorLiterals } from './literals'

export interface ChatComposerEditorProps {
  submitOnEnter?: boolean
  id: string
  label: string
  placeholder: string
  input: React.RefObject<HTMLTextAreaElement>
  value: string
  expanded: boolean
  hidden: boolean
  disabled: boolean
  onChange: (text: string) => void
  onSend: () => void
  onCancel: () => void
  onToggleExpanded?: () => void
}

export const ChatComposerEditor: React.FC<ChatComposerEditorProps> = ({
  submitOnEnter = true,
  input,
  value,
  expanded,
  hidden,
  disabled,
  id,
  label,
  placeholder,
  onChange,
  onSend,
  onCancel,
  onToggleExpanded,
}) => {
  const t = useLocaleText(editorLiterals)

  const resizeScroll = useRef<{ top: number; atEnd: boolean }>()

  useLayoutEffect(() => {
    const node = input.current
    const position = resizeScroll.current
    if (!node || !position) return

    // Keep the last visible line when collapsing a draft already scrolled to its end.
    node.scrollTop = position.atEnd ? node.scrollHeight : position.top
    resizeScroll.current = undefined
  }, [expanded, input])

  return (
    <div className={[styles.composerEditor, expanded && styles.isExpanded].filter(Boolean).join(' ')} hidden={hidden}>
      <TextareaAutosize
        ref={input}
        id={id}
        aria-label={label}
        placeholder={placeholder}
        minRows={expanded ? 18 : 2}
        maxRows={expanded ? 18 : 6}
        value={value}
        disabled={disabled}
        readOnly={hidden}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && hidden) {
            event.preventDefault()
            onCancel()
          }
          if (submitOnEnter && event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault()
            onSend()
          }
        }}
      />
      {!hidden && onToggleExpanded && (
        <Button
          variant="icon"
          className={styles.composerResize}
          aria-label={expanded ? t.collapse : t.expand}
          title={expanded ? t.collapse : t.expand}
          aria-expanded={expanded}
          aria-controls={id}
          isDisabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            const node = input.current
            if (node) {
              resizeScroll.current = {
                top: node.scrollTop,
                atEnd: node.scrollTop > 0 && node.scrollHeight - node.clientHeight - node.scrollTop <= 1,
              }
            }
            onToggleExpanded()
            node?.focus({ preventScroll: true })
          }}
        >
          {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </Button>
      )}
    </div>
  )
}
